import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useLoading } from "../../context/LoadingContext";
import dashboardApi from "../../api/dashboard";
import { fetchCarriers, fetchGroups } from "../../api/filterType";
import {
  DEFAULT_OUTCOMES,
  DEFAULT_CAREGIVERS,
  DEFAULT_PROGRAMS,
  OUTCOME_LABELS,
} from "./DashboardConstants";
import PropTypes from "prop-types";

import { successGreen, incompleteRed } from "../../constants/colors";
import { usePermissions } from "../../redux/permissions";

const DashboardContext = createContext(null);

export const DashboardProvider = ({ children }) => {
  const { showLoading, hideLoading } = useLoading();

  // --- User & Role Logic ---
  const userDetails = useMemo(
    () => JSON.parse(localStorage.getItem("userDetails") || "{}"),
    [],
  );
  const userRole = userDetails?.primaryRoleName;
  const { canRead: isAuthorized } = usePermissions("Care Guide Performance");

  // --- Shared Module State ---
  const [carrierFilter, setCarrierFilter] = useState(
    () => JSON.parse(localStorage.getItem("dashboard_carrierFilter") || "[]")
  );
  const [groupFilter, setGroupFilter] = useState(
    () => JSON.parse(localStorage.getItem("dashboard_groupFilter") || "[]")
  );
  const [carriers, setCarriers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedCarrierIds, setSelectedCarrierIds] = useState(
    () => JSON.parse(localStorage.getItem("dashboard_selectedCarrierIds") || "[]")
  );
  const [selectedGroupIds, setSelectedGroupIds] = useState(
    () => JSON.parse(localStorage.getItem("dashboard_selectedGroupIds") || "[]")
  );
  const [carrierToGroupsMap, setCarrierToGroupsMap] = useState({});
  const [groupToCarrierMap, setGroupToCarrierMap] = useState({});

  const [dateRange, setDateRange] = useState(() => {
    const stored = localStorage.getItem("dashboard_dateRange");
    return stored ? JSON.parse(stored) : { startDate: null, endDate: null };
  });

  const [carrierSearch, setCarrierSearch] = useState(
    () => localStorage.getItem("dashboard_carrierSearch") || ""
  );
  const [groupSearch, setGroupSearch] = useState(
    () => localStorage.getItem("dashboard_groupSearch") || ""
  );

  const [apiDateRange, setApiDateRange] = useState({
    minDate: null,
    maxDate: null,
  });
  const [loading, setLoading] = useState(true);
  const [dropdownLoading, setDropdownLoading] = useState({
    CARRIER: false,
    GROUP: false,
  });
  const [dropdownHasMore, setDropdownHasMore] = useState({
    CARRIER: true,
    GROUP: true,
  });
  const [pagination, setPagination] = useState({ CARRIER: 0, GROUP: 0 });

  useEffect(() => {
    localStorage.setItem("dashboard_carrierFilter", JSON.stringify(carrierFilter));
    localStorage.setItem("dashboard_groupFilter", JSON.stringify(groupFilter));
    localStorage.setItem("dashboard_selectedCarrierIds", JSON.stringify(selectedCarrierIds));
    localStorage.setItem("dashboard_selectedGroupIds", JSON.stringify(selectedGroupIds));
    localStorage.setItem("dashboard_dateRange", JSON.stringify(dateRange));
    localStorage.setItem("dashboard_carrierSearch", carrierSearch);
    localStorage.setItem("dashboard_groupSearch", groupSearch);
  }, [
    carrierFilter,
    groupFilter,
    selectedCarrierIds,
    selectedGroupIds,
    dateRange,
    carrierSearch,
    groupSearch,
  ]);

  // Data
  const [outcomes, setOutcomes] = useState(() => DEFAULT_OUTCOMES);
  const [caregivers, setCaregivers] = useState(() => DEFAULT_CAREGIVERS);
  const [programPerf, setProgramPerf] = useState(DEFAULT_PROGRAMS);
  const [memberOpp, setMemberOpp] = useState({
    total: "-",
    complete: "-",
    incomplete: "-",
  });

  // Logic Handlers
  const fetchOptions = useCallback(
    async (type, search = "", replace = false) => {
      setDropdownLoading((prev) => ({ ...prev, [type]: true }));
      try {
        const params = {
          outreachType: "MEMBER_OUTREACH",
          search: search.trim(),
          filterType: type,
          startDate: dateRange.startDate || "",
          endDate: dateRange.endDate || "",
          claim_type: "PAID",
        };
        if (type === "CARRIER") {
          let res = await fetchCarriers(params);
          if (userRole === "L6")
            res = res.filter(
              (i) => !i.carrierName?.toLowerCase().includes("communicare"),
            );
          else if (userRole === "L7")
            res = res.filter(
              (i) =>
                !i.carrierName?.toLowerCase().includes("communicare") &&
                !i.carrierName?.toLowerCase().includes("lucy"),
            );
          else if (userRole === "L8")
            res = res.filter(
              (i) =>
                i.carrierName?.toLowerCase().includes("communicare") ||
                i.rxGroups?.some((g) =>
                  g.groupName?.toLowerCase().includes("communicare"),
                ),
            );

          const cToG = {};
          const gToC = {};
          res.forEach((i) => {
            const cDisplay = `${i.carrierName} (${i.carrierCode})`;
            const opts =
              i.rxGroups?.map((g) => ({
                groupNumber: g.groupNumber,
                groupName: g.groupName,
              })) || [];
            cToG[cDisplay] = opts;
            opts.forEach((g) => {
              gToC[`${g.groupName} (${g.groupNumber})`] = {
                code: i.carrierCode,
                display: cDisplay,
              };
            });
          });
          setCarrierToGroupsMap((prev) => ({ ...prev, ...cToG }));
          setGroupToCarrierMap((prev) => ({ ...prev, ...gToC }));
          setCarriers((prev) =>
            replace ? Object.keys(cToG) : [...prev, ...Object.keys(cToG)],
          );
          setDropdownHasMore((prev) => ({
            ...prev,
            CARRIER: res.length === 30,
          }));
        } else {
          const res = await fetchGroups(params);
          const gToC = {};
          res.forEach((i) => {
            if (i.groupName && i.groupNumber)
              gToC[`${i.groupName} (${i.groupNumber})`] = {
                code: i.carrierCode,
                display: `${i.carrierName} (${i.carrierCode})`,
              };
          });
          setGroupToCarrierMap((prev) => ({ ...prev, ...gToC }));
          setGroups((prev) =>
            replace ? Object.keys(gToC) : [...prev, ...Object.keys(gToC)],
          );
          setDropdownHasMore((prev) => ({ ...prev, GROUP: res.length === 30 }));
        }
      } catch (e) {
        console.error(e);
      } finally {
        setDropdownLoading((prev) => ({ ...prev, [type]: false }));
      }
    },
    [userRole, dateRange.startDate, dateRange.endDate],
  );

  const updateApiDateRange = useCallback((startDate, endDate) => {
    if (startDate && endDate) {
      setApiDateRange((prev) => ({
        minDate: prev.minDate || startDate,
        maxDate: prev.maxDate || endDate,
      }));
    }
  }, []);

  const handleOutcomesResponse = useCallback(
    (outRes) => {
      if (!outRes) return;

      const raw = outRes.outcomeGroups || outRes || [];
      updateApiDateRange(outRes.startDate, outRes.endDate);

      const mappedOutcomes = raw.map((i) => {
        const labelKey = i.label || i.outcome;
        const labelText =
          labelKey in OUTCOME_LABELS
            ? OUTCOME_LABELS[labelKey]
            : labelKey || "Unknown";
        return {
          label: labelText,
          count: i.count ?? 0,
          color: i.colourCode || null,
          subReasons: i.subReasons || [],
        };
      });

      setOutcomes(mappedOutcomes);
    },
    [updateApiDateRange],
  );

  const handleProgramPerformanceResponse = useCallback(
    (perfRes) => {
      if (!perfRes) return;

      const performancesData = perfRes.programDetails || perfRes;
      const performances = Array.isArray(performancesData)
        ? performancesData
        : [];
      updateApiDateRange(perfRes.startDate, perfRes.endDate);
      setProgramPerf(performances);
    },
    [updateApiDateRange],
  );

  const handleMemberOpportunityResponse = useCallback(
    (memRes) => {
      if (!memRes) return;

      const oppData = {
        total: memRes.memberOpportunityTotal ?? "-",
        complete: memRes.memberOpportunityComplete ?? "-",
        incomplete: memRes.memberOpportunityIncomplete ?? "-",
      };
      setMemberOpp(oppData);

      // Store startDate and endDate from API response
      if (memRes.startDate && memRes.endDate) {
        const apiDateRange = {
          startDate: memRes.startDate,
          endDate: memRes.endDate,
        };
        localStorage.setItem(
          "dashboard_memberOppDateRange",
          JSON.stringify(apiDateRange),
        );
      }

      updateApiDateRange(memRes.startDate, memRes.endDate);
    },
    [updateApiDateRange],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    showLoading();
    try {
      const common = {
        carrierCodes: selectedCarrierIds,
        groupNumbers: selectedGroupIds,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };
      const [outRes, perfRes, memRes] = await Promise.all([
        dashboardApi.getOutreachOutcomes(common).catch(() => null),
        dashboardApi.getProgramPerformances(common).catch(() => null),
        dashboardApi.getMemberOpportunityOutcomes(common).catch(() => null),
      ]);

      handleOutcomesResponse(outRes);
      handleProgramPerformanceResponse(perfRes);
      handleMemberOpportunityResponse(memRes);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      hideLoading();
    }
  }, [
    dateRange,
    selectedCarrierIds,
    selectedGroupIds,
    showLoading,
    hideLoading,
    handleOutcomesResponse,
    handleProgramPerformanceResponse,
    handleMemberOpportunityResponse,
  ]);

  const fetchCareGuides = useCallback(async () => {
    if (!isAuthorized) return;
    try {
      const res = await dashboardApi
        .getCareGuidePerformance()
        .catch(() => null);
      if (res) {
        const caretakers = Array.isArray(res) ? res : [];
        setCaregivers(caretakers);
        localStorage.setItem(
          "dashboard_caregivers",
          JSON.stringify(caretakers),
        );
      }
    } catch (e) {
      console.error(e);
    }
  }, [isAuthorized]);

  useEffect(() => {
    fetchCareGuides();
  }, [fetchCareGuides]);

  // Derived Memos
  const memberOverviewCalculated = useMemo(() => {
    const { total, complete, incomplete } = memberOpp;
    return {
      total,
      complete,
      incomplete,
      pieData: [
        {
          name: "Complete",
          value: typeof complete === "number" ? complete : 0,
          color: successGreen,
        },
        {
          name: "Incomplete",
          value: typeof incomplete === "number" ? incomplete : 0,
          color: incompleteRed,
        },
      ],
    };
  }, [memberOpp]);

  const contextValue = useMemo(
    () => ({
      userRole,
      isAuthorized,
      userDetails,
      fetchData,
      fetchOptions,
      loading,
      outcomes,
      caregivers,
      programPerf,
      memberOpp,
      carrierFilter,
      setCarrierFilter,
      groupFilter,
      setGroupFilter,
      carriers,
      groups,
      selectedCarrierIds,
      setSelectedCarrierIds,
      selectedGroupIds,
      setSelectedGroupIds,
      dateRange,
      setDateRange,
      dropdownLoading,
      dropdownHasMore,
      pagination,
      setPagination,
      carrierToGroupsMap,
      groupToCarrierMap,
      memberOverviewCalculated,
      setLoading,
      apiDateRange,
      carrierSearch,
      setCarrierSearch,
      groupSearch,
      setGroupSearch,
    }),
    [
      userRole,
      isAuthorized,
      userDetails,
      fetchData,
      fetchOptions,
      loading,
      outcomes,
      caregivers,
      programPerf,
      memberOpp,
      carrierFilter,
      groupFilter,
      carriers,
      groups,
      selectedCarrierIds,
      selectedGroupIds,
      dateRange,
      dropdownLoading,
      dropdownHasMore,
      pagination,
      carrierToGroupsMap,
      groupToCarrierMap,
      memberOverviewCalculated,
      apiDateRange,
      carrierSearch,
      groupSearch,
    ],
  );

  return (
    <DashboardContext.Provider value={contextValue}>
      {children}
    </DashboardContext.Provider>
  );
};

DashboardProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context)
    throw new Error("useDashboard must be used within DashboardProvider");
  return context;
};
